import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { securitySelector } from '@/core/services/App/AppSelector'
import { getSecurityInfoThunk, onSetEmpty } from '@/core/services/App/AppSlice'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { userSelector } from '@/modules/Auth/store/AuthSelector'
import { getUserThunk } from '@/modules/Auth/store/AuthSlice'
import { cn } from '@bem-react/classname'

import { useHandleServerError } from '@/hooks/useHandleServerError'

import { Checkbox } from '@/components/Form/Checkbox'
import { Field } from '@/components/Form/Field'
import { FieldPhone } from '@/components/Form/FieldPhone'
import { Button } from '@/components/UI/Button'
import { Card } from '@/components/UI/Card'
import Icon from '@/components/UI/Icon'
import { Loading } from '@/components/UI/Loading'
import { Title } from '@/components/UI/Title'

import './Settings.scss'
import { FormEmail } from './components/FormEmail'

const cnSettings = cn('Settings')

export const Settings: FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { handleServerError } = useHandleServerError()

  const security = useAppSelector(securitySelector)
  const user = useAppSelector(userSelector)

  const [isLoading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    getSecurityInfo()
    dispatch(onSetEmpty(true))

    return () => {
      dispatch(onSetEmpty(false))
    }
  }, [])

  const getSecurityInfo = async () => {
    try {
      await dispatch(getSecurityInfoThunk())
      setLoading(false)
    } catch (error) {
      handleServerError(error)
      setLoading(false)
    }
  }

  const onSuccess = async () => {
    try {
      await dispatch(getUserThunk())
    } catch (error) {
      handleServerError(error)
    }
  }

  return (
    <>
      {isLoading ? (
        <Loading type='circle' minHeight='300px' />
      ) : (
        <div className={cnSettings()}>
          {user && user.is_verify === 0 && (
            <Card className={cnSettings('card')}>
              <Title>{t('settings-verification')}</Title>

              <div className={cnSettings('top')}>
                <div className={cnSettings('left')}>
                  <div className={cnSettings('icon')}>
                    <Icon name='verification' />
                  </div>
                  <div className={cnSettings('text')}>
                    {t('settings-verification-text')}
                  </div>
                </div>
                <Button color='blue' onClick={() => navigate('/c/verify')}>
                  {t('settings-start-verification')}
                </Button>
              </div>
            </Card>
          )}
          <div className={cnSettings('group')}>
            <Card className={cnSettings('card')}>
              <Title>{t('settings-profile')}</Title>

              {user && (
                <div className={cnSettings('form')}>
                  <FormEmail email={user.email} onSuccess={onSuccess} />
                  <FieldPhone mb='none' value={user.phone} />
                  {user.first_name && (
                    <Field
                      name=''
                      label={t('settings-name')}
                      mb='none'
                      value={user.first_name}
                      disabled
                    />
                  )}
                  {user.last_name && (
                    <Field
                      name=''
                      label={t('settings-last-name')}
                      mb='none'
                      value={user.last_name}
                      disabled
                    />
                  )}
                </div>
              )}
            </Card>
            <Card className={cnSettings('card')}>
              {security && (
                <>
                  <Title>2FA</Title>

                  <div className={cnSettings('wrap')}>
                    <div className={cnSettings('qr')}>
                      <img src={security.qr} alt='2FA' />
                    </div>

                    <Field
                      name=''
                      label='Secret key'
                      iconLeft={<Icon name='key' />}
                      value={security.secret}
                    />
                  </div>
                  <Checkbox
                    id={'fa_enable'}
                    name={'fa_enable'}
                    checked={!!security.fa_enable}
                    label='Enable 2FA authorization'
                  />
                  <div className={cnSettings('form')}>
                    <Field name='' label='Enter code' mb='none' />
                    <Button fluid>Save changes</Button>
                  </div>
                </>
              )}
            </Card>
          </div>
        </div>
      )}
    </>
  )
}
